import React, { useEffect, useRef, useState, useMemo } from "react";
import DateFilter from "./DateFilter";
import dayjs, { Dayjs } from "dayjs";
import Header from "../../components/dms_common/Header";
import "./DeliveryConfirmation.css";
import { fetchLoadoutDetails } from "./DeliveryConfirmationService";
import DeliverySummary from "./DeliverySummary";
import { DeliveryInvoiceSummary, LoadOutDetail, LoadoutDetailsType } from "../../types/types";
import { Loader } from "../../components/loader/Loader";
import LoadoutDeliveryTable from "./loadoutDeliveryTable";
import { logLoadTrace } from "../../firebase";
import { getMetaData } from "../../services/uiConfigService";
import { fetchLoadoutConfigs } from "../../components/loadouts/pre-seller/PresellerLoadoutService";
import useSessionStorageDates from "../../components/dms_common/UseSessionStorageDates";
import MultiCategoryFilter from "../../components/dms_common/MultiCategoryFilter";
import DayEndModal from "../../components/dms_common/DayEndPopup";
import DayEndNavigator from "../../components/dms_common/DayEndNavigator";
import { getDayEndStatusFromSession, getOutForDeliveryInvoiceNumber, updateActivityStatus } from "../../components/dms_common/DayEndUtils";

const LoadoutDeliveryConfirmation = () => {
  const [summary, setSummary] = useState<DeliveryInvoiceSummary>({
    total: 0,
    pending: 0,
    delivered: 0,
    partiallyDelivered: 0,
    cancelled: 0,
  });
  const [allData, setAllData] = useState<LoadoutDetailsType[]>([]);
  const [searchText, setSearchText] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("ALL");
  const defaultStartDate = dayjs().subtract(7, 'day');
  const defaultEndDate = dayjs().subtract(0, 'day');

  let initialStartDate, initialEndDate;
  
  const dayEndObj = getDayEndStatusFromSession();

  if (dayEndObj && dayEndObj.isDayEndInProgress){
    initialStartDate = dayjs(dayEndObj.startDate)
    initialEndDate = dayjs(dayEndObj.currentDateAndTime)
  } else{
    initialStartDate = defaultStartDate;
    initialEndDate = defaultEndDate;
  }

  const { storedStartDate, storedEndDate, storeSessionStorageDates } = useSessionStorageDates("loadout_delivery_confirmation_dates", initialStartDate, initialEndDate);
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: storedStartDate.format("YYYY-MM-DD"),
    endDate: storedEndDate.format("YYYY-MM-DD"),});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const isPartialDeliveryAllowed = useRef<boolean>();
  const enableSalesmanAssignment = useRef<boolean>(false);
  const [filterOpen, setFilterOpen] = useState<boolean>(false);
  const [selectedFilterType, setSelectedFilterType] = useState<string>("status");
  const [selectedStatusTypes, setSelectedStatusTypes] = useState<string[]>([]);
  const {fetchPricingBySalesrep} = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
  const [filterSearchValue, setFilterSearchValue] = useState<string>("");

  const filterData = (
    data: LoadoutDetailsType[],
    searchText: string,
    statusFilter: string,
    selectedStatusTypes: string[]
  ) => {
    let filtered = [...data];
    if (statusFilter !== "ALL") {
      filtered = filtered.filter((invoice) => invoice.status?.toLowerCase() === statusFilter.toLowerCase());
    }
    if (selectedStatusTypes.length > 0 && !selectedStatusTypes.includes("ALL")) {
      filtered = filtered.filter((invoice) =>
        selectedStatusTypes.includes(invoice.status?.toUpperCase() || "")
      );
    }
    if (searchText) {
      const searchKey = searchText.toLowerCase();
      filtered = filtered.filter(
        (invoice) =>
          invoice.salesmanId?.toLowerCase().includes(searchKey) ||
          invoice.loadNumber?.toLowerCase().includes(searchKey) ||
          invoice.salesmanName?.toLowerCase().includes(searchKey)
      );
    }
    return filtered;
  };

  const filteredData = useMemo(() => {
    return filterData(allData, searchText, statusFilter, selectedStatusTypes);
  }, [allData, searchText, statusFilter, selectedStatusTypes]);

  // Calculate summary for the status types
  const calculateSummary = (invoiceDetails: LoadoutDetailsType[]) => {
    const allInvoices: LoadOutDetail[]  = invoiceDetails.map(x=>x.loadOutDetails).flat()
    const total = allInvoices.length;
    const pending = allInvoices.filter((invoice) => invoice.salesInfo.invoiceStatus.toLowerCase() === "invoiced" || invoice.salesInfo.invoiceStatus.toLowerCase() === "out for delivery").length;
    // const pending = allInvoices.filter((invoice) => invoice.status.toLowerCase() === "out for delivery").length;
    const delivered = allInvoices.filter((invoice) => invoice.salesInfo.invoiceStatus.toLowerCase() === "delivered").length;
    const partiallyDelivered = allInvoices.filter((invoice) => invoice.salesInfo.invoiceStatus.toLowerCase() === "partial delivered").length;
    const cancelled = allInvoices.filter((invoice) => invoice.salesInfo.invoiceStatus.toLowerCase() === "cancelled").length;
    setSummary({ total, pending, delivered, partiallyDelivered, cancelled });
  };

 // Fetch data when dateRange changes
 useEffect(() => {
  const fetchFilteredData = async () => {
    if(isPartialDeliveryAllowed.current == undefined){
      isPartialDeliveryAllowed.current = await getPartialConfig();
    }
    try{
      const displayLoadoutSummaryValue = await fetchLoadoutConfigs(
        "loadoutConfigs"
      );
      enableSalesmanAssignment.current = displayLoadoutSummaryValue?.Preseller?.EnableSalesmanAssignment
    }catch(e){}
    try {
      setIsLoading(true);
      let loadoutData = await fetchLoadoutDetails(dateRange.startDate, dateRange.endDate);
      loadoutData = loadoutData.filter(loadout=>loadout.status.toLowerCase() == "in-transit" || loadout.status.toLowerCase() == "delivered");

      //if dayend is in progress then only show those loadouts in which if any of the invoice is in the dayEnd mandation then the loadout will be shown
      if (dayEndObj && dayEndObj.isDayEndInProgress) {
        const outForDeliveryInvoices: string[] = await getOutForDeliveryInvoiceNumber();
        loadoutData = loadoutData.filter((l) =>
          l.loadOutDetails.some(inv =>
            outForDeliveryInvoices.includes(inv.invoiceNumber)
          )
        );
      }

      setAllData(loadoutData);
      calculateSummary(loadoutData); // Update summary
    } catch (error) {
      console.error("Error fetching filtered data:", error);
    }
    setIsLoading(false);
  };

  logLoadTrace(fetchFilteredData,"loadout_delivery_confirmation_page_load_time");
  updateActivityStatus("delivery", "inProgress");
}, [dateRange]);

const getPartialConfig = async() => {
  try{
      const configs = await getMetaData("clientconfig","settlement_configuration");
      return configs.find((config:any)=>config.name == "isPartialDeliveryAllowed").value;
  }catch(error){}
  return true;
}

  const handleApply = (startDate: Dayjs, endDate: Dayjs) => {
    setDateRange({
      startDate: startDate.format("YYYY-MM-DD"),
      endDate: endDate.format("YYYY-MM-DD"),
    });
  };

  const handleReset = () => {
    setDateRange({
      startDate: dayjs().subtract(7, 'day').format("YYYY-MM-DD"),
      endDate: dayjs().format("YYYY-MM-DD"),
    });
  };

  const refreshData = ()=>{
    setDateRange({
      startDate: dateRange.startDate,
      endDate: dateRange.endDate
    });
  }

  const statusFilterMenuItems = [
    {
      id: "IN-TRANSIT",
      label: "In-Transit",
      count: allData.filter((invoice) => invoice.status?.toUpperCase() === "IN-TRANSIT").length
    },
    {
      id: "DELIVERED",
      label: "Delivered",
      count: allData.filter((invoice) => invoice.status?.toUpperCase() === "DELIVERED").length
    }
  ];

    const filters = [
    {
      id: "status",
      label: "Status",
      options: statusFilterMenuItems,
      selectedOptions: selectedStatusTypes,
      setSelectedOptions: setSelectedStatusTypes,
    },
  ];

  return (
    <div className="delivery-confirmation-container">
      <Header title={"Delivery Confirmation"} searchFunction={setSearchText} />
      <div className="delivery-confirmation-container-date">
        <DateFilter dayEndCheck={true} onApply={handleApply} onReset={handleReset} maxDays={30} featureId="loadout_delivery_confirmation" pageId="loadout_delivery_confirmation" />
        <DayEndNavigator currentPage="deliveryConfirmation"/>
      </div>
    
      <DayEndModal/>
      {/* Delivery Summary */}
      <div className="delivery-summary-filter">
        <DeliverySummary invoiceSummary={summary}/>
        {  
          (<div className="sale-return-filter">
                  <MultiCategoryFilter
                    isOpen={filterOpen}
                    setIsOpen={setFilterOpen}
                    activeFilter={selectedFilterType}
                    setActiveFilter={setSelectedFilterType}
                    filters={filters}
                    label="Type & Status"
                    searchValue={filterSearchValue}
                    onSearchChange={setFilterSearchValue}
                    showSearch={true}
                    // onFilterChange={handleApplyFilter}
                  />
          </div>)
        }
      </div>

      {isLoading ? 
        <Loader />
      :
        <>
          {/* Delivery Table */}
          <LoadoutDeliveryTable data={filteredData} refreshData={refreshData} isPartialDeliveryAllowed={isPartialDeliveryAllowed.current!=undefined ? isPartialDeliveryAllowed.current : true} enableSalesmanAssignment={enableSalesmanAssignment.current}/>
        </>
      }
    </div>
  );
};

export default LoadoutDeliveryConfirmation;
